import {
  DEFAULT_STROKE_OPTIONS,
  BRUSH_PRESETS,
  BULLET_INDENT_EM,
  applyPath,
  arrowHeadGeometry,
  connectorGeometry,
  contrastText,
  drawWorldPoints,
  embedSrc,
  fitRichTextToBox,
  fitTextToBox,
  layoutRichText,
  layoutText,
  pointAtFraction,
  rectCenter,
  richFontString,
  shapePath,
  shapeTextRect,
  strokeCenterline,
  strokeOutline,
  type BoardNode,
  type ConnectorNode,
  type DrawNode,
  type EmbedNode,
  type FrameNode,
  type ImageNode,
  type NotebookNode,
  type NodeLookup,
  notebookPaper,
  type Rect,
  type ShapeNode,
  type StickyNode,
  type StrokeStyle,
  type TextNode,
  type TextStyle,
  type Vec,
} from '@kanvas/board-core';
import { getImage, imageFailed } from './imageCache';
import { measureGeneration, measureText } from './measure';
import { scratch } from '../state/scratch';

export interface PaintEnv {
  lookup: NodeLookup;
  /** Current zoom, used to decide when detail can be skipped. */
  zoom: number;
  /** Suppresses interactive chrome (frame labels stay, hover does not). */
  forExport?: boolean;
  /**
   * Node whose text is being edited by the inline editor. Its text is skipped
   * here so the DOM textarea is the only copy on screen.
   */
  editingId?: string | null;
  /** Image currently being cropped — paints the full source for the crop UI. */
  croppingId?: string | null;
  /** Notebook opened for editing; closed cover is dimmed. */
  enteredNotebookId?: string | null;
  /**
   * Fraction of a stroke or connector to paint, 0..1. Used by the draw-on
   * animation that replays what a peer just drew; undefined means paint it all.
   */
  reveal?: number;
}

export const STICKY_PADDING = 14;
export const TEXT_PADDING = 4;
export const FRAME_LABEL_SIZE = 13;

function dashPattern(style: StrokeStyle, width: number): number[] {
  switch (style) {
    case 'dashed':
      return [Math.max(width * 3, 6), Math.max(width * 2.2, 5)];
    case 'dotted':
      return [0.01, Math.max(width * 2.2, 4)];
    default:
      return [];
  }
}

function withRotation(ctx: CanvasRenderingContext2D, node: BoardNode, paint: () => void): void {
  if (node.rotation === 0 && node.opacity === 1) {
    paint();
    return;
  }
  ctx.save();
  if (node.opacity !== 1) ctx.globalAlpha *= node.opacity;
  if (node.rotation !== 0) {
    const c = rectCenter({ x: node.x, y: node.y, w: node.w, h: node.h });
    ctx.translate(c.x, c.y);
    ctx.rotate(node.rotation);
    ctx.translate(-c.x, -c.y);
  }
  paint();
  ctx.restore();
}

/** Clip in the frame's own box, then undo the rotation so drawing stays in board space. */
function clipToFrame(ctx: CanvasRenderingContext2D, frame: FrameNode, zoom: number): void {
  const box = { x: frame.x, y: frame.y, w: frame.w, h: frame.h };
  // Keep the frame stroke visible; children paint above the fill otherwise.
  const inset = Math.min(0.5 / Math.max(zoom, 0.05), box.w / 4, box.h / 4);
  if (frame.rotation !== 0) {
    const c = rectCenter(box);
    ctx.translate(c.x, c.y);
    ctx.rotate(frame.rotation);
    ctx.translate(-c.x, -c.y);
  }
  ctx.beginPath();
  ctx.rect(box.x + inset, box.y + inset, Math.max(box.w - inset * 2, 0), Math.max(box.h - inset * 2, 0));
  ctx.clip();
  if (frame.rotation !== 0) {
    const c = rectCenter(box);
    ctx.translate(c.x, c.y);
    ctx.rotate(-frame.rotation);
    ctx.translate(-c.x, -c.y);
  }
}

/** Frames with `clip` hide the part of a child that sticks out of the frame. */
function withAncestorClip(
  ctx: CanvasRenderingContext2D,
  node: BoardNode,
  lookup: NodeLookup,
  zoom: number,
  paint: () => void,
): void {
  const frames: FrameNode[] = [];
  const seen = new Set<string>();
  let parentId = node.parentId;
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = lookup(parentId);
    if (!parent) break;
    if (parent.type === 'frame' && parent.clip) frames.push(parent);
    parentId = parent.parentId;
  }
  if (frames.length === 0) {
    paint();
    return;
  }
  ctx.save();
  for (let i = frames.length - 1; i >= 0; i--) clipToFrame(ctx, frames[i]!, zoom);
  paint();
  ctx.restore();
}

function roundRectPath(ctx: CanvasRenderingContext2D, r: Rect, radius: number): void {
  const rad = Math.max(0, Math.min(radius, Math.min(r.w, r.h) / 2));
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(r.x, r.y, r.w, r.h, rad);
    return;
  }
  ctx.moveTo(r.x + rad, r.y);
  ctx.arcTo(r.x + r.w, r.y, r.x + r.w, r.y + r.h, rad);
  ctx.arcTo(r.x + r.w, r.y + r.h, r.x, r.y + r.h, rad);
  ctx.arcTo(r.x, r.y + r.h, r.x, r.y, rad);
  ctx.arcTo(r.x, r.y, r.x + r.w, r.y, rad);
  ctx.closePath();
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

interface TextPaintOptions {
  /** Shrink the font until the text fits the box. */
  autoFit?: boolean;
  /** Skip painting when the glyphs would be sub-pixel anyway. */
  minPixelSize?: number;
}

/**
 * Wrapped layouts, keyed by the style object and then by text and box.
 *
 * Wrapping parses the markup and measures every word, and an auto-fitted box
 * repeats that for each font size it tries. Doing it for every text on screen
 * on every frame is what made a board with a lot of writing lag while panning.
 * Styles are immutable parts of immutable nodes, so they key a WeakMap safely;
 * the whole cache is dropped when font metrics change.
 */
type CachedLayout = ReturnType<typeof layoutRichText> | ReturnType<typeof layoutText>;
let layoutCache = new WeakMap<TextStyle, Map<string, CachedLayout>>();
let layoutCacheGeneration = measureGeneration();
/** Per style; a resize drag produces a new width every frame. */
const LAYOUTS_PER_STYLE = 16;

function cachedLayout<T extends CachedLayout>(
  style: TextStyle,
  key: string,
  build: () => T,
): T {
  const generation = measureGeneration();
  if (generation !== layoutCacheGeneration) {
    layoutCache = new WeakMap();
    layoutCacheGeneration = generation;
  }
  let entries = layoutCache.get(style);
  if (!entries) {
    entries = new Map();
    layoutCache.set(style, entries);
  }
  const hit = entries.get(key);
  if (hit) return hit as T;
  const layout = build();
  if (entries.size >= LAYOUTS_PER_STYLE) entries.clear();
  entries.set(key, layout);
  return layout;
}

export function paintText(
  ctx: CanvasRenderingContext2D,
  text: string,
  style: TextStyle,
  box: Rect,
  zoom: number,
  options: TextPaintOptions = {},
): void {
  if (text === '') return;

  const minPx = options.minPixelSize ?? 4;
  // Below a few device pixels the text is a grey smudge; a filled bar reads
  // better and costs one rect instead of dozens of glyph rasterisations.
  if (style.fontSize * zoom < minPx) {
    const layout = cachedLayout(style, `plain|${box.w}|${text}`, () =>
      layoutText(text, style, box.w, measureText),
    );
    ctx.save();
    ctx.globalAlpha *= 0.45;
    ctx.fillStyle = style.color;
    const lh = layout.lineHeight;
    layout.lines.forEach((line, i) => {
      if (line.width === 0) return;
      ctx.fillRect(box.x, box.y + i * lh + lh * 0.28, Math.min(line.width, box.w), lh * 0.42);
    });
    ctx.restore();
    return;
  }

  const layout = options.autoFit
    ? cachedLayout(style, `fit|${box.w}|${box.h}|${text}`, () =>
        fitRichTextToBox(text, style, box, measureText),
      )
    : cachedLayout(style, `rich|${box.w}|${text}`, () =>
        layoutRichText(text, style, box.w, measureText),
      );

  const totalHeight = layout.height;
  let top: number;
  switch (style.valign) {
    case 'top':
      top = box.y;
      break;
    case 'bottom':
      top = box.y + box.h - totalHeight;
      break;
    default:
      top = box.y + (box.h - totalHeight) / 2;
  }
  const baselineOffset = layout.lineHeight * 0.5 + layout.fontSize * 0.34;
  const bulletIndent = BULLET_INDENT_EM * layout.fontSize;

  ctx.save();
  ctx.textBaseline = 'alphabetic';
  // Assigning `font` makes the canvas parse the string again even when it is
  // unchanged, and most runs share one face.
  let currentFont = '';
  const setFont = (font: string) => {
    if (font === currentFont) return;
    ctx.font = font;
    currentFont = font;
  };

  layout.lines.forEach((line, i) => {
    const indent = line.bullet ? bulletIndent : 0;
    const lineWidth = line.width + indent;
    let x: number;
    switch (style.align) {
      case 'right':
        x = box.x + box.w - lineWidth;
        break;
      case 'center':
        x = box.x + (box.w - lineWidth) / 2;
        break;
      default:
        x = box.x;
    }
    const y = top + i * layout.lineHeight + baselineOffset;

    if (line.bullet && line.words.length > 0) {
      setFont(richFontString(style, { bold: style.bold, italic: style.italic }, layout.fontSize));
      ctx.fillStyle = style.color;
      ctx.fillText('•', x, y);
    }
    x += indent;

    for (const word of line.words) {
      setFont(
        richFontString(
          style,
          { bold: style.bold || word.bold, italic: style.italic || word.italic },
          layout.fontSize,
        ),
      );
      // Linked words pull towards the product accent rather than to a
      // generic hyperlink blue, but keep some of the author's own colour so a
      // link inside coloured text does not jump out of it.
      ctx.fillStyle = word.link ? mixColor(style.color, '#4f46e5', 0.6) : style.color;
      ctx.fillText(word.text, x, y);

      const wantsUnderline = style.underline || word.link !== null;
      if (wantsUnderline || word.strike || style.strike) {
        const underlineY = y + layout.fontSize * 0.14;
        const strikeY = y - layout.fontSize * 0.3;
        ctx.strokeStyle = ctx.fillStyle as string;
        ctx.lineWidth = Math.max(layout.fontSize * 0.07, 0.6);
        if (wantsUnderline) {
          ctx.beginPath();
          ctx.moveTo(x, underlineY);
          ctx.lineTo(x + word.width, underlineY);
          ctx.stroke();
        }
        if (word.strike || style.strike) {
          ctx.beginPath();
          ctx.moveTo(x, strikeY);
          ctx.lineTo(x + word.width, strikeY);
          ctx.stroke();
        }
      }
      x += word.width;
    }
  });
  ctx.restore();
}

/** Blends a hex-ish CSS color toward another; used to tint link runs. */
function mixColor(base: string, tint: string, amount: number): string {
  const parse = (c: string): [number, number, number] | null => {
    if (!c.startsWith('#')) return null;
    const hex =
      c.length === 4
        ? c
            .slice(1)
            .split('')
            .map((ch) => ch + ch)
            .join('')
        : c.slice(1);
    const n = parseInt(hex, 16);
    if (Number.isNaN(n) || hex.length !== 6) return null;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const a = parse(base);
  const b = parse(tint);
  if (!a || !b) return tint;
  const mix = a.map((v, i) => Math.round(v + (b[i]! - v) * amount));
  return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
}

/**
 * Trims a polyline to a fraction of its own arc length.
 *
 * Cutting by *length* rather than by point index is what makes a reveal travel
 * at a constant speed: freehand samples are dense on slow curves and sparse on
 * fast straights, so an index-based cut would race through the straights.
 */
function trimPolyline(points: readonly Vec[], fraction: number): Vec[] {
  if (fraction >= 1 || points.length < 2) return points as Vec[];
  if (fraction <= 0) return [];

  let total = 0;
  const segments: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const d = Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
    segments.push(d);
    total += d;
  }
  if (total === 0) return [points[0]!];

  let remaining = total * fraction;
  const out: Vec[] = [points[0]!];
  for (let i = 0; i < segments.length; i++) {
    const d = segments[i]!;
    if (remaining >= d) {
      out.push(points[i + 1]!);
      remaining -= d;
      continue;
    }
    const k = d === 0 ? 0 : remaining / d;
    out.push({
      x: points[i]!.x + (points[i + 1]!.x - points[i]!.x) * k,
      y: points[i]!.y + (points[i + 1]!.y - points[i]!.y) * k,
    });
    break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Individual node types
// ---------------------------------------------------------------------------

function paintSticky(ctx: CanvasRenderingContext2D, node: StickyNode, env: PaintEnv): void {
  const box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };

  ctx.save();
  // A soft drop shadow is what makes a sticky read as a physical note.
  ctx.shadowColor = 'rgba(15, 20, 30, 0.18)';
  ctx.shadowBlur = 10 * Math.min(env.zoom, 1.5);
  ctx.shadowOffsetY = 3 * Math.min(env.zoom, 1.5);
  ctx.fillStyle = node.fill;
  roundRectPath(ctx, box, 4);
  ctx.fill();
  ctx.restore();

  if (env.editingId === node.id) return;

  const textBox: Rect = {
    x: box.x + STICKY_PADDING,
    y: box.y + STICKY_PADDING,
    w: Math.max(box.w - STICKY_PADDING * 2, 1),
    h: Math.max(box.h - STICKY_PADDING * 2, 1),
  };
  paintText(ctx, node.text, node.textStyle, textBox, env.zoom, { autoFit: !node.autoGrow });
}

function paintShape(ctx: CanvasRenderingContext2D, node: ShapeNode, env: PaintEnv): void {
  const box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };
  const path = shapePath(node.shape, box, node.style.radius);

  ctx.beginPath();
  applyPath(path, ctx);

  if (node.style.fillStyle === 'solid' && node.style.fill !== 'transparent') {
    if (node.style.shadow) {
      ctx.save();
      ctx.shadowColor = 'rgba(15, 20, 30, 0.22)';
      ctx.shadowBlur = 14 * Math.min(env.zoom, 1.5);
      ctx.shadowOffsetY = 4 * Math.min(env.zoom, 1.5);
      ctx.fillStyle = node.style.fill;
      ctx.fill();
      ctx.restore();
    } else {
      ctx.fillStyle = node.style.fill;
      ctx.fill();
    }
  }

  if (node.style.strokeWidth > 0) {
    ctx.save();
    ctx.strokeStyle = node.style.stroke;
    ctx.lineWidth = node.style.strokeWidth;
    ctx.lineJoin = 'round';
    ctx.lineCap = node.style.strokeStyle === 'dotted' ? 'round' : 'butt';
    ctx.setLineDash(dashPattern(node.style.strokeStyle, node.style.strokeWidth));
    ctx.stroke();
    ctx.restore();
  }

  if (node.text !== '' && env.editingId !== node.id) {
    paintText(ctx, node.text, node.textStyle, shapeTextRect(node.shape, box), env.zoom, {
      autoFit: true,
    });
  }
}

function paintTextNode(ctx: CanvasRenderingContext2D, node: TextNode, env: PaintEnv): void {
  const box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };
  if (node.background) {
    ctx.fillStyle = node.background;
    roundRectPath(ctx, box, 4);
    ctx.fill();
  }
  if (env.editingId === node.id) return;
  paintText(
    ctx,
    node.text,
    node.textStyle,
    {
      x: box.x + TEXT_PADDING,
      y: box.y,
      w: Math.max(box.w - TEXT_PADDING * 2, 1),
      h: box.h,
    },
    env.zoom,
  );
}

/**
 * Tessellated strokes, keyed by node object.
 *
 * Nodes are immutable snapshots — an edit replaces the object — so identity
 * is a safe cache key and a WeakMap lets deleted strokes go with their node.
 * Without it every visible stroke was re-smoothed and re-outlined on every
 * frame, which on a board with hundreds of strokes made drawing stutter.
 * Both shapes are kept because which one paints depends on the zoom.
 */
const strokePathCache = new WeakMap<DrawNode, { outline?: Path2D; line?: Path2D }>();

function drawInput(node: DrawNode, world: readonly { x: number; y: number }[]) {
  return world.map((p, i) => [p.x, p.y, node.points[i]?.[2] ?? 0.5] as [number, number, number]);
}

function polylinePath(points: readonly { x: number; y: number }[], close: boolean): Path2D {
  const path = new Path2D();
  if (points.length === 0) return path;
  path.moveTo(points[0]!.x, points[0]!.y);
  for (let i = 1; i < points.length; i++) path.lineTo(points[i]!.x, points[i]!.y);
  if (close) path.closePath();
  return path;
}

function paintDraw(ctx: CanvasRenderingContext2D, node: DrawNode, env: PaintEnv): void {
  // A reveal animation trims the stroke per frame, so only settled strokes
  // are cached.
  const cacheable = env.reveal === undefined;
  const cached = cacheable ? strokePathCache.get(node) : undefined;

  // Below a couple of screen pixels the outline is indistinguishable from a
  // plain stroked line, and stroking is far cheaper.
  const screenWidth = node.size * env.zoom;
  const asLine = screenWidth < 2.5 && !node.closed;

  let path = asLine ? cached?.line : cached?.outline;
  if (!path) {
    const full = drawWorldPoints(node);
    const world = env.reveal !== undefined ? trimPolyline(full, env.reveal) : full;
    if (world.length === 0) return;
    const preset = BRUSH_PRESETS[node.brush] ?? {};
    const strokeOpts = { ...DEFAULT_STROKE_OPTIONS, ...preset, size: node.size };
    path = asLine
      ? polylinePath(strokeCenterline(drawInput(node, world), { ...strokeOpts, smoothing: 0 }), false)
      : polylinePath(strokeOutline(drawInput(node, world), strokeOpts), true);
    if (cacheable) {
      const entry = cached ?? {};
      if (asLine) entry.line = path;
      else entry.outline = path;
      strokePathCache.set(node, entry);
    }
  }

  ctx.save();
  if (node.brush === 'highlighter') {
    ctx.globalAlpha *= 0.4;
    ctx.globalCompositeOperation = 'multiply';
  }
  if (asLine) {
    ctx.strokeStyle = node.color;
    ctx.lineWidth = node.size;
    // Round, like the outline's caps, so a stroke does not grow square ends
    // the moment the zoom crosses the threshold.
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke(path);
  } else {
    ctx.fillStyle = node.color;
    ctx.fill(path);
  }
  ctx.restore();
}

function paintImage(ctx: CanvasRenderingContext2D, node: ImageNode, env: PaintEnv): void {
  const cropping = !env.forExport && env.croppingId === node.id;
  const draftCrop = cropping ? (scratch.cropDraft ?? node.crop ?? { x: 0, y: 0, w: 1, h: 1 }) : null;
  const crop = draftCrop ?? node.crop;
  const mask =
    cropping && scratch.cropMaskDraft && scratch.cropMaskDraft.length >= 3
      ? scratch.cropMaskDraft
      : node.mask;

  // While cropping, expand to the full source box so the user can see what is
  // being cut away; the crop overlay draws the dim mask and handles.
  let box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };
  if (cropping && draftCrop) {
    // Scale comes from the saved crop (what node.x/y/w/h describe); the
    // draft only positions the crop window, painted by the overlay.
    const saved = node.crop ?? { x: 0, y: 0, w: 1, h: 1 };
    const scaleX = node.w / Math.max(saved.w * node.naturalW, 1e-6);
    const scaleY = node.h / Math.max(saved.h * node.naturalH, 1e-6);
    box = {
      x: node.x - saved.x * node.naturalW * scaleX,
      y: node.y - saved.y * node.naturalH * scaleY,
      w: node.naturalW * scaleX,
      h: node.naturalH * scaleY,
    };
  }

  const image = getImage(node.src);

  if (!image) {
    ctx.save();
    ctx.fillStyle = imageFailed(node.src) ? '#FBE3E3' : '#E8EBF0';
    roundRectPath(ctx, box, node.radius);
    ctx.fill();
    ctx.strokeStyle = 'rgba(20,25,35,0.12)';
    ctx.lineWidth = 1 / env.zoom;
    ctx.stroke();
    if (env.zoom > 0.3) {
      ctx.fillStyle = '#8A93A2';
      ctx.font = `500 ${12}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        imageFailed(node.src) ? 'Изображение недоступно' : 'Загрузка…',
        box.x + box.w / 2,
        box.y + box.h / 2,
      );
    }
    ctx.restore();
    return;
  }

  ctx.save();
  if (node.radius > 0 && !cropping) {
    roundRectPath(ctx, { x: node.x, y: node.y, w: node.w, h: node.h }, node.radius);
    ctx.clip();
  }

  // Freehand mask: clip to the polygon mapped into the destination box.
  if (!cropping && mask && mask.length >= 3) {
    const c = crop ?? { x: 0, y: 0, w: 1, h: 1 };
    ctx.beginPath();
    for (let i = 0; i < mask.length; i++) {
      const p = mask[i]!;
      const dx = node.x + ((p.x - c.x) / Math.max(c.w, 1e-6)) * node.w;
      const dy = node.y + ((p.y - c.y) / Math.max(c.h, 1e-6)) * node.h;
      if (i === 0) ctx.moveTo(dx, dy);
      else ctx.lineTo(dx, dy);
    }
    ctx.closePath();
    ctx.clip();
  }

  if (cropping) {
    ctx.drawImage(image, box.x, box.y, box.w, box.h);
  } else if (crop) {
    const sx = crop.x * image.naturalWidth;
    const sy = crop.y * image.naturalHeight;
    const sw = crop.w * image.naturalWidth;
    const sh = crop.h * image.naturalHeight;
    ctx.drawImage(image, sx, sy, sw, sh, node.x, node.y, node.w, node.h);
  } else {
    ctx.drawImage(image, node.x, node.y, node.w, node.h);
  }
  ctx.restore();
}

function paintNotebook(ctx: CanvasRenderingContext2D, node: NotebookNode, env: PaintEnv): void {
  const box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };
  const spine = Math.min(14, box.w * 0.06);

  ctx.save();

  // Always paint the closed cover on the board — writing happens in NotebookWorkspace.
  ctx.fillStyle = node.coverColor;
  roundRectPath(ctx, box, 6);
  ctx.fill();

  ctx.fillStyle = 'rgba(40,50,70,0.12)';
  ctx.fillRect(box.x, box.y, spine, box.h);

  ctx.strokeStyle = 'rgba(40,50,70,0.18)';
  ctx.lineWidth = 1.5 / env.zoom;
  roundRectPath(ctx, box, 6);
  ctx.stroke();

  const fontSize = Math.min(22, Math.max(12, box.w * 0.09));
  ctx.fillStyle = '#1A1F28';
  ctx.font = `700 ${fontSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(node.title || 'Тетрадь', box.x + box.w / 2, box.y + box.h * 0.42, box.w - spine - 16);

  ctx.font = `500 ${Math.max(10, fontSize * 0.55)}px Inter, system-ui, sans-serif`;
  ctx.fillStyle = '#6B7482';
  const paper = notebookPaper(node);
  const paperHint =
    paper === 'grid' ? 'в клетку' : paper === 'lined' ? 'в линейку' : 'без линий';
  ctx.fillText(
    `${node.pages.length} стр. · ${paperHint}`,
    box.x + box.w / 2,
    box.y + box.h * 0.55,
    box.w - spine - 16,
  );

  ctx.restore();
}

function paintFrame(ctx: CanvasRenderingContext2D, node: FrameNode, env: PaintEnv): void {
  const box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };
  ctx.fillStyle = node.fill;
  ctx.fillRect(box.x, box.y, box.w, box.h);
  ctx.strokeStyle = 'rgba(20, 25, 35, 0.16)';
  ctx.lineWidth = 1 / env.zoom;
  ctx.strokeRect(box.x, box.y, box.w, box.h);

  // The title sits in screen pixels so it stays readable at any zoom.
  if (env.zoom > 0.05) {
    const size = FRAME_LABEL_SIZE / env.zoom;
    ctx.save();
    ctx.fillStyle = '#6B7482';
    ctx.font = `600 ${size}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(node.name, box.x, box.y - size * 0.45);
    ctx.restore();
  }
}

function paintEmbed(ctx: CanvasRenderingContext2D, node: EmbedNode, env: PaintEnv): void {
  const box: Rect = { x: node.x, y: node.y, w: node.w, h: node.h };
  // The live iframe is a DOM overlay; the canvas draws the frame and a label so
  // exports and zoomed-out views still show something meaningful.
  ctx.save();
  ctx.fillStyle = '#161A21';
  roundRectPath(ctx, box, 8);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 1 / env.zoom;
  ctx.stroke();
  if (env.zoom > 0.2) {
    ctx.fillStyle = '#9AA3B2';
    ctx.font = `500 ${13}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(node.title || embedSrc(node), box.x + box.w / 2, box.y + box.h / 2);
  }
  ctx.restore();
}

function paintComment(ctx: CanvasRenderingContext2D, node: BoardNode, env: PaintEnv): void {
  // Comment pins keep a constant screen size, like a map marker.
  const r = 11 / env.zoom;
  const cx = node.x;
  const cy = node.y;
  ctx.save();
  ctx.fillStyle = '#FFFFFF';
  ctx.strokeStyle = 'rgba(20,25,35,0.2)';
  ctx.lineWidth = 1 / env.zoom;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#F0435F';
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.45, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function paintConnector(ctx: CanvasRenderingContext2D, node: ConnectorNode, env: PaintEnv): void {
  const geo = connectorGeometry(node, env.lookup);
  const reveal = env.reveal ?? 1;

  ctx.save();
  ctx.strokeStyle = node.stroke;
  ctx.lineWidth = node.strokeWidth;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.setLineDash(dashPattern(node.strokeStyle, node.strokeWidth));

  ctx.beginPath();
  if (reveal >= 1) {
    applyPath(geo.path, ctx);
  } else {
    // A partial reveal has to be stroked from the sampled polyline: a bezier
    // cannot be cut at an arbitrary arc length without subdividing it.
    const partial = trimPolyline(geo.points, reveal);
    if (partial.length > 1) {
      ctx.moveTo(partial[0]!.x, partial[0]!.y);
      for (let i = 1; i < partial.length; i++) ctx.lineTo(partial[i]!.x, partial[i]!.y);
    }
  }
  ctx.stroke();
  ctx.setLineDash([]);

  // Arrowheads only make sense once the line has reached its endpoints, so
  // they fade in over the last stretch of the reveal instead of sliding along.
  const headAlpha = reveal >= 1 ? 1 : Math.max(0, (reveal - 0.75) / 0.25);
  if (headAlpha > 0) ctx.globalAlpha *= headAlpha;

  for (const [head, at, dir] of headAlpha > 0
    ? ([
        [node.startHead, geo.start, geo.startDir],
        [node.endHead, geo.end, geo.endDir],
      ] as const)
    : []) {
    const arrow = arrowHeadGeometry(head, at, dir, node.strokeWidth);
    if (!arrow) continue;
    ctx.beginPath();
    applyPath(arrow.path, ctx);
    if (arrow.filled) {
      ctx.fillStyle = node.stroke;
      ctx.fill();
    } else {
      ctx.lineWidth = node.strokeWidth;
      ctx.stroke();
    }
  }
  ctx.restore();

  if (node.text !== '' && env.editingId !== node.id && reveal >= 1) {
    const mid = pointAtFraction(geo.points, 0.5);
    const layout = cachedLayout(node.textStyle, `plain|260|${node.text}`, () =>
      layoutText(node.text, node.textStyle, 260, measureText),
    );
    const pad = 5;
    const box: Rect = {
      x: mid.x - layout.width / 2 - pad,
      y: mid.y - layout.height / 2 - pad,
      w: layout.width + pad * 2,
      h: layout.height + pad * 2,
    };
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.94)';
    roundRectPath(ctx, box, 4);
    ctx.fill();
    ctx.restore();
    paintText(
      ctx,
      node.text,
      { ...node.textStyle, align: 'center', valign: 'middle' },
      box,
      env.zoom,
    );
  }
}

/** Paints one node in board coordinates. The caller owns the camera transform. */
export function paintNode(ctx: CanvasRenderingContext2D, node: BoardNode, env: PaintEnv): void {
  if (node.type === 'group') return; // Groups are containers, children paint themselves.

  withAncestorClip(ctx, node, env.lookup, env.zoom, () => {
    withRotation(ctx, node, () => {
      switch (node.type) {
        case 'sticky':
          paintSticky(ctx, node, env);
          break;
        case 'shape':
          paintShape(ctx, node, env);
          break;
        case 'text':
          paintTextNode(ctx, node, env);
          break;
        case 'draw':
          paintDraw(ctx, node, env);
          break;
        case 'image':
          paintImage(ctx, node, env);
          break;
        case 'notebook':
          paintNotebook(ctx, node, env);
          break;
        case 'frame':
          paintFrame(ctx, node, env);
          break;
        case 'embed':
          paintEmbed(ctx, node, env);
          break;
        case 'comment':
          paintComment(ctx, node, env);
          break;
        case 'connector':
          paintConnector(ctx, node, env);
          break;
      }
    });
  });
}

/** Text colour a newly created node should use on a given fill. */
export function textColorFor(fill: string): string {
  return fill === 'transparent' ? '#1A1F28' : contrastText(fill);
}

/** Board-space point at the centre of a node, used to place popovers. */
export function nodeAnchorPoint(node: BoardNode): Vec {
  return rectCenter({ x: node.x, y: node.y, w: node.w, h: node.h });
}
